import { ReactNode, useState, useEffect, FormEvent } from "react";
import { useEvent } from "client-util/useEvent";
import { ROUND_IDS } from "sockapi/scope-specs/round-spec";
import { LargeSpinner } from "components/Loading";
import { Link } from "components/Link";
import { useNavStore, useParsedSlug } from "stores/NavStore";
import "./IslandFinder.css";

export const DESERT_ISLAND_NUM_PIECES = 10000;
export const DESERT_ISLAND_PIECE_SLUG_PREFIX = "desert_island";
export const DESERT_ISLAND_ROUND_ID = ROUND_IDS.desert;

type DesertPieceDownloadResult = {
  pieceIndex: number;
} & (
  | {
      type: "coord";
      val: string;
    }
  | {
      type: "maze";
      url: string;
    }
  | {
      type: "sudoku";
      html: string;
    }
  | {
      type: "error";
      status: number;
    }
);

export function IslandFinder() {
  const [selectPieceInputVal, setSelectPieceInputVal] = useState("");
  const { desertIslandPieceId: pieceIndex } = useParsedSlug() ?? {};
  const setRedirectCommand = useNavStore((state) => state.setRedirectCommand);
  const [pieceDownloadResult, setPieceDownloadResult] =
    useState<DesertPieceDownloadResult | null>(null);

  const selectPieceInputValNum = ((): number | null => {
    if (!/^\d+$/.test(selectPieceInputVal)) return null;
    const pieceIndex = Number(selectPieceInputVal);
    if (pieceIndex < 1 || pieceIndex > DESERT_ISLAND_NUM_PIECES) return null;
    return pieceIndex;
  })();

  const navigateToPieceInputVal = useEvent((e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (selectPieceInputValNum === null) return;
    setRedirectCommand({
      path: `/puzzle/${DESERT_ISLAND_PIECE_SLUG_PREFIX}_${selectPieceInputValNum}`,
      isManual: true,
    });
  });

  useEffect(() => {
    if (pieceIndex === undefined) return;
    let isCanceled = false;
    const urlNoExt = `/static/desert_island/piece_${pieceIndex.toString().padStart(5, "0")}`;
    switch (pieceIndex % 3) {
      case 0:
        (async () => {
          const res = await fetch(`${urlNoExt}.txt`);
          if (isCanceled) return;
          if (!res.ok) {
            setPieceDownloadResult({
              pieceIndex,
              type: "error",
              status: res.status,
            });
            return;
          }
          const val = await res.text();
          if (isCanceled) return;
          setPieceDownloadResult({ pieceIndex, type: "coord", val });
        })().catch(console.error);
        break;
      case 1:
        setPieceDownloadResult({
          pieceIndex,
          type: "maze",
          url: `${urlNoExt}.png`,
        });
        break;
      case 2:
        (async () => {
          const res = await fetch(`${urlNoExt}.txt`);
          if (isCanceled) return;
          if (!res.ok) {
            setPieceDownloadResult({
              pieceIndex,
              type: "error",
              status: res.status,
            });
            return;
          }
          const html = await res.text();
          if (isCanceled) return;
          setPieceDownloadResult({ pieceIndex, type: "sudoku", html });
        })().catch(console.error);
        break;
      default:
        throw new Error("invalid pieceIndex modulo 3");
    }
    return () => {
      isCanceled = true;
    };
  }, [pieceIndex]);

  if (pieceIndex === undefined) return <LargeSpinner inline={true} />;

  const scrollButtons: ReactNode[] = [];
  for (let i = -3; i <= 3; i++) {
    if (i === 0) continue;
    const nextPieceIndex = pieceIndex + i;
    const isDisabled =
      nextPieceIndex < 1 || nextPieceIndex > DESERT_ISLAND_NUM_PIECES;
    const nextSlug = !isDisabled
      ? `${DESERT_ISLAND_PIECE_SLUG_PREFIX}_${nextPieceIndex}`
      : DESERT_ISLAND_PIECE_SLUG_PREFIX;
    scrollButtons.push(
      <Link
        key={i}
        route="puzzle"
        params={{ slug: nextSlug }}
        disabled={isDisabled}
      >
        <button disabled={isDisabled} type="button">
          {i > 0 ? `+${i}` : `${i}`}
        </button>
      </Link>
    );
  }

  const pieceContents = ((): ReactNode => {
    if (pieceDownloadResult === null) return <LargeSpinner inline={true} />;
    switch (pieceDownloadResult.type) {
      case "error": {
        return `Failed to download piece contents: status ${pieceDownloadResult.status}. (This is not a puzzle.)`;
      }
      case "coord": {
        return <div>{pieceDownloadResult.val}</div>;
      }
      case "maze": {
        return <img src={pieceDownloadResult.url + "?1"} />;
      }
      case "sudoku": {
        return (
          <div
            dangerouslySetInnerHTML={{ __html: pieceDownloadResult.html }}
          ></div>
        );
      }
    }
    // Don't return anything. If we hit this case, we should hit an
    // error typechecking the return value.
  })();

  return (
    <div className="puzzle-page-body" style={{ textAlign: "center" }}>
      <h1>Piece {pieceIndex}</h1>
      <form onSubmit={navigateToPieceInputVal}>
        <span className="button-squish-zone">{scrollButtons}</span>{" "}
        <input
          type="number"
          min="1"
          max={DESERT_ISLAND_NUM_PIECES}
          placeholder="Go to piece..."
          style={{ width: "10em" }}
          onChange={(e) => setSelectPieceInputVal(e.target.value)}
          autoFocus
        />{" "}
        <button disabled={selectPieceInputValNum === null} type="submit">
          Go!
        </button>
      </form>
      {pieceContents}
    </div>
  );
}
